<template>
  <div
    class="main-product component-section critical-component-clear"
    :class="{ [$class('loading')]: !getComponentActive('main-product') || state === 'loading' }"
  >
    <div class="grid">
      <div class="col xs4 l8">
        <product-gallery
          :additional-image="additionalImage"
          :container-context="containerContext"
          :model-description="modelDescription"
          :namespace="namespace"
          :number-of-images="liquid.numberOfImages"
          :product="product"
          :variant="variant"
        />
      </div>

      <div class="col xs4 l4 offset-left-l">
        <div class="main-product__details">
          <div class="main-product__details-header">
            <product-review-stars
              :product="product"
              :product-reviews="productReviews"
              :review-count="reviewCount"
              :variant="variant"
              :colour-swatch-name="colourSwatchName"
              @update-variant="handleVariantUpdate"
            />

            <div
              v-if="productFitBadge"
              class="main-product__fit-badge"
            >
              <span
                class="text-body-2xs main-product__fit-badge-label"
                v-text="productFitBadge"
              />
            </div>
          </div>

          <h1
            :id="`section-title-${liquid.sectionId}`"
            class="main-product__title text-heading-s"
            v-text="product.title || liquid.title"
          />

          <!-- Product title and prices -->
          <div class="main-product__prices-container">
            <product-prices
              :available="product?.available || product.isComingSoon"
              block-class="main-product"
              :hidden-sale-price="hiddenSalePrice"
              :compare-at-price="variant?.compareAtPrice?.amount || product?.compareAtPrice?.amount"
              :price="variant?.price?.amount || product?.price?.amount || liquid.price"
              :show-red-price="priceInRed"
            />
          </div>

          <!-- Colour swatches -->
          <div
            v-if="colourSwatchName && colourFamilySize"
            class="main-product__colour-swatches"
          >
            <colour-swatches
              :colour-family-size="colourFamilySize"
              :current-swatch-name="currentSwatchName || colourSwatchName"
              :product="product"
              @product-id-update="updateProductHandle"
              @update-variant="handleVariantUpdate"
            />
          </div>

          <!-- Main product form -->
          <div class="main-product__form">
            <product-form
              :liquid="liquid"
              :product="product"
              :product-options="productOptions"
              :variant="variant"
              :product-gift-card="productGiftCard"
              :accelerated-checkout-button="acceleratedCheckoutButton"
              :coming-soon-link="comingSoonLink"
              :hidden-sale-price="hiddenSalePrice"
              :size-guide-link="sizeGuideLink"
              @update-variant="handleVariantUpdate"
              @product-id-update="updateProductHandle"
            />
          </div>

          <div
            v-if="product"
            class="main-product__paired-product-sets"
          >
            <paired-product-sets
              :product="product"
              :current-handle="product.handle"
              :title="liquid.productPairSetsTitle"
            />
          </div>

          <div class="main-product__alternative-payments">
            <alternative-payments
              v-if="
                alternativePayments.clear_pay_text
                  || alternativePayments.klarna_text
                  || alternativePayments.pay_pal_text
              "
              :alternative-payments="alternativePayments"
              :price="variant?.price?.amount || product?.price?.amount || liquid.price"
            />
          </div>

          <div
            v-if="showDeliveryInformation || showAfterShip"
            class="main-product__delivery-returns-information"
          >
            <delivery-and-returns-information
              v-if="showDeliveryInformation"
              :standard-delivery-text="liquid.standardDeliveryText"
              :free-delivery-threshold-text="liquid.freeDeliveryThresholdText"
              :shipping-price="shippingPrice"
              :free-shipping-threshold="freeShippingThreshold"
            />

            <aftership
              v-if="showAfterShip"
              :product="product"
            />
          </div>

          <div class="main-product__description">
            <accordion
              :panel-data="accordionContent"
              :fallback="{
                title: $string('product.content.product_details'),
                content: liquid.description
              }"
            />
          </div>
        </div>
      </div>
    </div>

    <products-by-tag
      :product="product"
      @products-by-tag="(val) => productsByTag = val"
      @products-by-tag-loaded="(val) => productsByTagLoaded = val"
    />
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} additionalImage - Setting with placeholder image to make up to even image numbers.
 * @param {String, Number} colourFamilySize - Number of available colourways.
 * @param {String} handle - Product page's original handle.
 * @param {String} liquid.description - Product's Liquid description.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {String} liquid.price - Product's Liquid price.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Product's Liquid title.
 * @param {String} liquid.standardDeliveryText - Standard delivery & returns information.
 * @param {String} liquid.freeDeliveryThresholdText - Free delivery threshold information.
 * @param {Boolean} showDeliveryInformation - Show delivery information.
 * @param {Boolean} showAfterShip - Show AfterShip.
 * @param {String} modelDescription - String describing the model in pdp images.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Array} productOptions - Array of product option objects.
 * @param {Object} productReviews - Object with review data taken from product metafields.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {String} colourSwatchName - Colour swatch name of the product.
 * @param {String} shippingPrice - Shipping price.
 * @param {String} freeShippingThreshold - Free shipping threshold price.
 * @param {String} comingSoonLink - Coming soon landing page link.
 *
 */
import Accordion from '~async/accordion/accordion'
import ColourSwatches from '~async/colour-swatches/colour-swatches'
import ProductsByTag from './products-by-tag.vue'
import ProductForm from '~async/product-form/product-form'
import ProductPrices from '~global/product-prices/product-prices'
import ProductReviewStars from '~async/product-review-stars/product-review-stars'
import ProductGallery from '~async/product-gallery/product-gallery'

import product from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'
import { getHiddenSalePrice } from '~/helpers/general'

import { mapActions, mapState } from 'vuex'
import AlternativePayments
  from '~async/alternative-payments/alternative-payments.vue'
import DeliveryAndReturnsInformation
  from '~async/delivery-returns-information/delivery-returns-information'
import Aftership from '~async/aftership/aftership.vue'
import PairedProductSets
  from '~async/paired-product-sets/paired-product-sets.vue'

export default {
  name: 'MainProduct',

  components: {
    Aftership,
    AlternativePayments,
    DeliveryAndReturnsInformation,
    Accordion,
    ColourSwatches,
    ProductsByTag,
    ProductForm,
    ProductGallery,
    ProductPrices,
    ProductReviewStars,
    PairedProductSets,
  },

  props: {
    colourFamilySize: {
      type: [Number, String],
      default: 6,
    },
    additionalImage: {
      type: Object,
      default: () => {},
    },
    containerContext: {
      type: String,
      default: 'pdp',
    },
    colourSwatchName: {
      type: [Boolean, String],
      default: false,
    },
    handle: {
      type: [Boolean, String],
      default: cnvs.page.handle,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        hasOnlyDefaultVariant: false,
        numberOfImages: 6,
        pdpReturns: '',
        price: 0,
        sectionId: '',
        title: '',
        productPairSetsTitle: '',
        standardDeliveryText: '',
        freeDeliveryThresholdText: '',
      }),
    },
    showDeliveryInformation: {
      type: Boolean,
      default: true,
    },
    showAfterShip: {
      type: Boolean,
      default: true,
    },
    modelDescription: {
      type: String,
      default: '',
    },
    product: {
      type: [Object, Boolean],
      default: false,
    },
    productFit: {
      type: [Boolean, String],
      default: false,
    },
    productFitBadge: {
      type: [Boolean, String],
      default: false,
    },
    garmentMaterial: {
      type: [Boolean, String],
      default: false,
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    productReviews: {
      type: Object,
      default: () => {},
    },
    productGiftCard: {
      type: Boolean,
      default: false,
    },
    acceleratedCheckoutButton: {
      type: Boolean,
      default: false,
    },
    comingSoonLink: {
      type: String,
      default: '',
    },
    alternativePayments: {
      type: Object,
      default: () => {},
    },
    shippingPrice: {
      type: String,
      default: '',
    },
    freeShippingThreshold: {
      type: String,
      default: '',
    },
    sizeGuideLink: {
      type: String,
      default: '',
    },
  },

  emits: ['update-request-object'],

  data() {
    return {
      currentHandle: cnvs.page.handle,
      namespace: 'MainProduct',
      productsByTag: null,
      productsByTagLoaded: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      variant: (state) => state.product.variant,
    }),

    /**
     * Returns the current products swatch name.
     * @returns {String}
     */
     currentSwatchName() {
      const swatch = this.product?.metafields?.find(field => field?.name === 'swatch_name')

      if (!swatch) {
        return ''
      }

      return swatch.value
    },

    /**
     * Returns a calculated image ratio.
     * @returns {String, Boolean}
     */
    imageRatio(){
      return this.state === 'loading' || !product.giftCard ? '3:4' : false
    },

    /**
     * Returns the review count from the current product.
     * @returns {Number}
     */
    reviewCount() {
      const count = this.product?.metafields?.find(
        field => field.name === 'review_rating_count',
      )

      if (!count?.value) {
        return 0
      }

      return parseInt(count.value, 10)
    },

    /**
     * Construct the accordion content from the available data.
     * @returns {Array} - Array of accordion panel objects.
     */
    accordionContent() {
      const content = []

      if (this.product?.description) {

        const internalSku = this.product?.metafields?.find(field => field.name === 'internal_sku')
        const sku = internalSku ? `
          <div class="main-product__description-sku">
            <span class="text-body-2xs">${this.$string('product.content.sku')}</span>
            <span class="text-body-2xs-bold">${internalSku.value}</span>
          </div>` : ''

        const descriptionString = `
          <div class="main-product__description-panel">
            ${sku}
            <div class="text-body-2xs main-product__description-content rte">
              ${this.product.description}
            </div>
          </div>
        `
        content.push({
          title: this.$string('product.content.product_details'),
          content: descriptionString,
        })
      }

      const washingInstructionsData = this.product?.metafields?.find(
        field => field.name === 'wash_care_instructions',
      )

      if (this.productFit) {
        content.push({
          title: this.$string('product.content.product_fit'),
          content: this.productFit,
        })
      }

      if (washingInstructionsData?.value?.length) {
        let washingInstructions = `<ul class="main-product__care-instructions">`

        washingInstructionsData?.value?.forEach(
          instruction => washingInstructions += `<li class="text-body-2xs">${instruction}</li>`,
        )
        washingInstructions += '</ul>'

        content.push({
          title: this.$string('product.content.care_instructions'),
          content: washingInstructions,
        })
      }

      if (this.liquid?.pdpReturns) {
        content.push({
          title: this.$string('product.content.delivery_returns'),
          content: this.liquid.pdpReturns,
        })
      }

      if (this.garmentMaterial) {
        content.push({
          title: this.$string('product.content.garment_material'),
          content: this.garmentMaterial,
        })
      }

      return content
    },

    /**
     * Determines whether the price should be shown in red.
     * @returns {Boolean}
     */
    priceInRed() {
      return this.product?.tags?.includes('red-price') || false
    },

    /**
     * Returns the hidden sale price.
     * @returns {String}
     */
    hiddenSalePrice() {
      return getHiddenSalePrice(this.product?.tags, this.$variable('store.currency'))
    },

    queryID() {
      return new URLSearchParams(location.search).get('queryID')
    },
  },

  watch: {

    /**
     * Watch currentHandle for update to load new product.
     */
    currentHandle() {
      this.queryProduct(this.currentHandle)
    },

    /**
     * Watch product to update it to vuex on change.
     */
    product(val) {
      this.setProduct(val)
    },
  },

  mounted() {
    this.queryProduct()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setProduct: 'product/setProduct',
      setVariant: 'product/setVariant',
    }),

    /**
     * Updates the current product handle when a new product is selected.
     * @param {Object} val - Object containing selected variant/product.
     */
    updateProductHandle(val) {
      const handle = val?.value?.handle

      if (!handle) {
        return
      }

      /**
       * Update the local and theme variables with the new handle.
       */
      this.currentHandle = handle
      cnvs.page.handle = handle

      /**
       * Update the browser history state.
       */
      const url = new URL(`https:${val.value.url}`)

      // If algolia queryID parameter exists.
      if (this.queryID) {
        url.searchParams.set('queryID', this.queryID)
      }

      history.replaceState({}, '', url.href)
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }

      if (this.product.handle === handle) {
        this.state = 'ready'
        return
      }

      try {
        const response = await cnvs.Query({
          query: product,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        const formattedProduct = formatProduct(response)

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        this.state = 'ready'
        cnvs.EventBus.emit('cnvs:product-updated', {
          response: formattedProduct,
          type: 'product',
        })

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.setVariant(variant)

      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const url = new URL(location.href)

      if (variant?.id) {
        url.searchParams.set('variant', variant.id)
      }

      // If algolia queryID parameter exists.
      if (this.queryID) {
        url.searchParams.set('queryID', this.queryID)
      }

      history.replaceState({
        html: '',
      }, '', url.href)
    },
  },
}
</script>

<style lang="scss">
@import './main-product';
</style>
